import React from "react";
import { useContent } from "@/lib/content";
import { useLang } from "@/lib/lang";
import { Linkedin, Share2, Twitter, Copy } from "lucide-react";
import { toast } from "sonner";

/** Author bio block + LinkedIn share button for the end of an insight post. */
export default function InsightAuthor({ insight }) {
  const { author_bios = [] } = useContent();
  const { t } = useLang();
  const I = t.insight || {};
  const fallback = { name: insight?.author || "", role: insight?.role || "", bio: "", linkedin: "", photo: "" };
  const matched = author_bios.find((a) => a?.name && insight?.author && a.name.toLowerCase().trim() === insight.author.toLowerCase().trim());
  const author = { ...fallback, ...(matched || {}) };

  const pageUrl = typeof window !== "undefined" ? window.location.href : "";
  const liShare = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(pageUrl)}`;
  const twShare = `https://twitter.com/intent/tweet?text=${encodeURIComponent(insight?.title || "")}&url=${encodeURIComponent(pageUrl)}`;

  const copyLink = async () => {
    try { await navigator.clipboard.writeText(pageUrl); toast.success(I.copy || "Link copiado."); }
    catch { toast.error("—"); }
  };

  return (
    <div className="mt-16 mb-16 md:mb-20 border-2 border-black/10 p-8 md:p-10 rounded-lg bg-neutral-50" data-testid="insight-author">
      <div className="text-xs uppercase tracking-widest text-neutral-500 mb-6">{I.authorBy || "Escrito por"}</div>
      <div className="grid md:grid-cols-12 gap-8 md:gap-10 items-start">
        <div className="md:col-span-2 flex md:block justify-center">
          {author.photo ? (
            <img src={author.photo} alt={author.name} className="w-28 h-28 md:w-32 md:h-32 rounded-full object-cover ring-4 ring-white shadow-lg" loading="lazy" />
          ) : (
            <div className="w-28 h-28 md:w-32 md:h-32 rounded-full bg-neutral-200 flex items-center justify-center text-2xl font-display text-neutral-500 ring-4 ring-white shadow-lg">{author.name?.split(" ").map((s) => s[0]).slice(0, 2).join("")}</div>
          )}
        </div>
        <div className="md:col-span-7 md:pl-4">
          <div className="font-display text-2xl md:text-3xl leading-tight">{author.name || "—"}</div>
          {author.role && <div className="text-xs uppercase tracking-widest text-accent-orange mt-2 font-semibold">{author.role}</div>}
          {author.bio && <p className="text-sm md:text-base text-neutral-700 leading-relaxed mt-5">{author.bio}</p>}
          {author.linkedin && (
            <a href={author.linkedin} target="_blank" rel="noopener noreferrer" data-testid="author-linkedin" className="inline-flex items-center gap-1.5 text-xs font-semibold mt-5 px-4 py-2 border border-black/15 rounded-full hover:bg-[#0a66c2] hover:text-white hover:border-transparent transition">
              <Linkedin className="w-3.5 h-3.5" /> {I.viewProfile || "Ver perfil no LinkedIn"}
            </a>
          )}
        </div>
        <div className="md:col-span-3 md:text-right md:border-l md:border-black/10 md:pl-6">
          <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-3 flex items-center md:justify-end gap-1.5"><Share2 className="w-3 h-3" /> {I.share || "Compartilhar"}</div>
          <div className="flex md:justify-end gap-2 flex-wrap">
            <a href={liShare} target="_blank" rel="noopener noreferrer" data-testid="share-linkedin" className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 bg-[#0a66c2] text-white rounded-full hover:opacity-90 transition">
              <Linkedin className="w-3.5 h-3.5" /> LinkedIn
            </a>
            <a href={twShare} target="_blank" rel="noopener noreferrer" data-testid="share-twitter" className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 border border-black/15 rounded-full hover:bg-black hover:text-white transition">
              <Twitter className="w-3.5 h-3.5" /> X
            </a>
            <button onClick={copyLink} data-testid="share-copy" className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 border border-black/15 rounded-full hover:bg-black hover:text-white transition">
              <Copy className="w-3.5 h-3.5" /> {I.copy || "Copiar"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
